import React, { useState, useEffect, forwardRef } from "react";
import styled from "styled-components";
import { formatCardNumber, formatExpirationDate } from "../../utils/formatters";
import { FlexContainer, Loader } from "@mindmemobile/mm-shared-global";
import NMIPaymentMethodForm, {
  NMIPaymentMethodFormHandle,
} from "@mindmemobile/mm-payment-modules/src/shared/components/NMIPaymentMethodForm";
import {
  CheckOutService,
  MerchantAccount,
  PaymentForm,
  PurchasePaymentIntentModel,
} from "@mindmemobile/mm-payment-modules";
import { CreateFundraiserModel } from "../../models";
import { LegalTermsAndConditionType } from "../../enums";
import ToastService from "../ToastService";

// Matches the checkbox used on the donor step: a green box with a white tick. A plain input
// renders with the browser's own tick (dark on light), which looks out of place here.
const SurchargeCheckbox = styled.input<{ checked?: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 2px solid ${(props) => (props.checked ? "#10b981" : "#e2e8f0")};
  background: ${(props) => (props.checked ? "#10b981" : "#ffffff")};
  appearance: none;
  margin: 0;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:checked::after {
    content: "✓";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #ffffff;
    font-size: 14px;
    font-weight: 700;
  }

  &:hover {
    border-color: #10b981;
  }
`;

const ConfirmationMessage = styled.div`
  font-size: 14px;
  color: #64748b;
  line-height: 1.5;
  text-align: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
`;

const ViewTermsLink = styled.button`
  background: none;
  border: none;
  color: #10b981;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  font-size: inherit;

  &:hover {
    text-decoration: underline;
  }
`;

interface PaymentInfo {
  cardNumber: string;
  expirationDate: string;
  securityCode: string;
  accountHolderName: string;
  accountType: string;
}

interface DonorInfo {
  firstName: string;
  lastName: string;
  email: string;
}

interface IDonationCapturePaymentMethodStep {
  amount: number;
  paymentIntent?: PurchasePaymentIntentModel;
  onPaymentMethodTokenUpdate: (token: string) => void;
  merchantAccount: MerchantAccount;
  accountId: string;
  frequency: "one-time" | "monthly";
  selectedFundraiser: CreateFundraiserModel;
  onCreatePaymentIntent?: (paymentIntent: PurchasePaymentIntentModel) => void;
  // Surcharge opt-in: shown only when the fundraiser charges a surcharge and asks the donor first.
  showSurchargeOption?: boolean;
  /** Surcharge applied without asking the donor, so it is stated rather than offered. */
  surchargeAutoApplied?: boolean;
  surchargeAmount?: number;
  coverSurcharge?: boolean;
  onToggleCoverSurcharge?: (value: boolean) => void;
  // The total actually charged (donation + any applied surcharge) - used in the terms text so it
  // matches the pay button. Falls back to `amount` when not provided.
  chargeAmount?: number;
}

export const DonationCapturePaymentMethodStep = forwardRef<
  NMIPaymentMethodFormHandle,
  IDonationCapturePaymentMethodStep
>((props, ref) => {
  const {
    frequency,
    amount,
    onPaymentMethodTokenUpdate,
    merchantAccount,
    accountId,
    selectedFundraiser,
    paymentIntent,
    showSurchargeOption,
    surchargeAutoApplied,
    surchargeAmount,
    coverSurcharge,
    onToggleCoverSurcharge,
    chargeAmount,
  } = props;

  const [showTerms, setShowTerms] = useState(false);

  const [isLoadingPaymentIntent, setIsLoadingPaymentIntent] = useState(true);
  useEffect(() => {
    getPaymentIntent();
  }, []);

  function getPaymentIntent() {
    if (!merchantAccount?.isFiservMerchantAccount() || paymentIntent) {
      setIsLoadingPaymentIntent(false);
      return;
    }
    CheckOutService.createPaymentIntent(
      "None",
      accountId,
      "",
      merchantAccount.createAPIModel2(),
    ).subscribe({
      next: (result) => {
        if (!result.result) {
          ToastService.showError("Error in creating payment intent");
        } else {
          const paymentIntent = PurchasePaymentIntentModel.deserializeAPIModel(
            result.result,
          );

          setIsLoadingPaymentIntent(false);
          props?.onCreatePaymentIntent?.(paymentIntent);
        }
      },
      error: () => {
        ToastService.showError("Error in creating payment intent");
      },
    });
  }

  const getFrequencyText = () => {
    return frequency === "monthly" ? " monthly starting today" : " today";
  };

  const formatAmount = (amount: number) => {
    // Show cents only when there are any, so a surcharged total ($100.60) is stated exactly in the
    // terms text while a round donation still reads "$100" rather than "$100.00".
    const hasCents = Math.round(amount * 100) % 100 !== 0;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0,
    }).format(amount);
  };
  if (merchantAccount?.isFiservMerchantAccount() && isLoadingPaymentIntent) {
    return <>{new Loader().loaderComponent}</>;
  }

  function renderTerms() {
    if (!selectedFundraiser.legalName) return null;

    function getTermsLinkOrText() {
      if (selectedFundraiser.termsType === LegalTermsAndConditionType.None)
        return null;

      switch (selectedFundraiser.termsType) {
        case LegalTermsAndConditionType.ReferenceURL:
          return (
            <ViewTermsLink
              onClick={() => {
                window.open(selectedFundraiser.getTermsUrlWithHttp(), "_blank");
              }}
            >
              View terms.
            </ViewTermsLink>
          );
        case LegalTermsAndConditionType.CustomTerms:
          return (
            <>
              {" "}
              {
                <ViewTermsLink onClick={() => setShowTerms(!showTerms)}>
                  {showTerms ? "Hide terms." : "View terms."}
                </ViewTermsLink>
              }
              {showTerms && <span>{selectedFundraiser.termsText}</span>}
            </>
          );
        default:
          return null;
      }
    }
    return (
      <>
        {" "}
        {
          <ConfirmationMessage>
            You allow <strong> {selectedFundraiser.legalName}</strong> to charge
            your <strong> account</strong> the amount of{" "}
            <strong>
              {" "}
              {formatAmount(chargeAmount ?? amount)}
              {getFrequencyText()}
            </strong>{" "}
            in accordance with our terms and conditions on our website.
            {getTermsLinkOrText()}
          </ConfirmationMessage>
        }
      </>
    );
  }
  return (
    <DonationCaptureWrapper style={{ marginBottom: "32px" }}>
      <FlexContainer
        fxLayout="column"
        className="donation-capture-payment-method-step"
      >
        {paymentIntent && merchantAccount.isFiservMerchantAccount() ? (
          <div className="payment-form-container">
            <FlexContainer
              justifyContent="center"
              alignItems="center"
              spacing={15}
            >
              <PaymentForm
                paymentIntent={paymentIntent}
                onPaymentSuccess={(token: string) => {
                  onPaymentMethodTokenUpdate &&
                    onPaymentMethodTokenUpdate(token);
                }}
                onPaymentError={() => {}}
                contact={null}
              />
            </FlexContainer>
          </div>
        ) : (
          <NMIPaymentMethodForm
            ref={ref}
            onPaymentSuccess={(token) => onPaymentMethodTokenUpdate(token)}
            onPaymentError={() => onPaymentMethodTokenUpdate("")}
            merchantAccount={merchantAccount}
            contact={null}
          />
        )}
      </FlexContainer>
      {renderTerms()}
      {showSurchargeOption && (
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginTop: "16px",
            cursor: "pointer",
            fontSize: "16px",
            color: "#1e293b",
          }}
        >
          <SurchargeCheckbox
            type="checkbox"
            checked={!!coverSurcharge}
            onChange={(e) => onToggleCoverSurcharge?.(e.target.checked)}
          />
          <span>
            Cover the <strong>{formatAmount(surchargeAmount || 0)}</strong> cost
            to process my donation
          </span>
        </label>
      )}
      {/* No checkbox in this case - the fundraiser applies the fee automatically - so the amount is
          stated instead, otherwise the donor sees a total above the amount they picked with nothing
          explaining the difference. */}
      {surchargeAutoApplied && (surchargeAmount || 0) > 0 && (
        <div
          style={{
            /* The terms block above already ends with 24px margin + 16px padding, so this sits
               close under it rather than adding a third gap on top of those. */
            marginTop: "-24px",
            fontSize: "14px",
            color: "#64748b",
            textAlign: "center",
          }}
        >
          Includes{" "}
          <strong style={{ color: "#1e293b" }}>
            {formatAmount(surchargeAmount || 0)}
          </strong>{" "}
          to cover the cost of processing your donation.
        </div>
      )}
    </DonationCaptureWrapper>
  );
});
export const DonationCaptureWrapper = styled.div`
  .donation-capture-payment-method-step {
    .payment-form-container {
      width: 100%;
      display: flex;
      justify-content: center;
    }

    .payment-form {
      width: 100% !important;
    }
    .payment-form.default-payment-form {
      min-height: 100px;
      iframe.CollectJSInlineIframe {
        input {
          height: 58px;
          border-radius: 12px;
          border: 2px solid #e2e8f0;
        }
      }
    }
  }
`;
